import { Dropdown } from 'antd';
import { useAppDispatch } from 'app/hooks';
import classNames from 'classnames';
import { ArrowDownIcon } from 'components/Icons';
import { RevxOverlay } from 'components/RevxOverlay';
import { Text } from 'components/RevxTypography';
import {
  FilterTabEnum,
  JobAdjustmentStatusEnum,
  JobItem,
  JobUIStatusEnum,
  JobWorkflowStatusEnum,
} from 'domains/workspaceDomain';
import { usePermissionFilterTabs } from 'hooks';
import { useJobStatusCollection } from 'hooks/useJobStatusCollection';
import { useEffect, useMemo, useState } from 'react';
import { TFuncKey, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import workspaceService from 'services/jobService';
import { JOB_ADJUSTMENT_WORKSPACE_FILTER } from 'utils/constants';
import { JobTabDisplay } from '../components/JobTabDisplay';
import { useRouteMatchFilter } from '../hooks/useRouteMatchFilter';
import cs from 'classnames';
import './WorkspaceFilterTabs.less';
import useJobCardInfo from '../hooks/useJobCardInfo';
import { useRequest } from 'ahooks';
import JobAdjustmentModal from '../components/JobAdjustmentModal';
import { useJobCancelJob } from '../hooks/useJobCancelJob';
import { getJobCardActions, JobCardActionEnum } from './jobCardActions';

type Props = {
  data: JobItem;
  defaultActiveTab?: FilterTabEnum | null;
  disabled?: boolean;
  disabledTabs?: FilterTabEnum[];
  onChange?: (tab: FilterTabEnum | null) => void;
};

type ActionMap = {
  text: string;
  handler: () => void;
};

const { AdjustJob, UpdateJob, CancelJob, SendToRFM } = JobCardActionEnum;

const { COMPLETED } = JobWorkflowStatusEnum;

export const WorkspaceFilterTabs = ({
  data,
  defaultActiveTab,
  disabled = false,
  disabledTabs = [],
  onChange,
}: Props) => {
  const { directlyAddToCart, workflowStatus } = data;
  const [curTab, setCurTab] = useState<FilterTabEnum | null>(
    defaultActiveTab || null
  );
  const [visible, setVisible] = useState<boolean>(false);
  const [jobAdjustmentModalVisible, setJobAdjustmentModalVisible] =
    useState<boolean>(false);
  const filterTabs = usePermissionFilterTabs(data);
  // console.log('data id ' + data.id + ' ,filter tabs:', filterTabs);
  const { defaultFilter } = useRouteMatchFilter();
  const history = useHistory();
  const { t } = useTranslation();
  const statusFilters = useJobStatusCollection();
  const { workspace, jobConfig, app } = useAppDispatch();
  const {
    onlyOneParticipant,
    hasCustomer,
    hasProxy,
    hasOperator,
    hasProducer,
    isInternalJob,
  } = useJobCardInfo(data);

  useEffect(() => {
    if (defaultActiveTab) {
      setCurTab(defaultActiveTab);
    } else {
      setCurTab(null);
    }
  }, [defaultActiveTab]);

  const jumpJobAdjustFn = async () => {
    console.log('jumpJobAdjustFn called with data:', data);
    console.log('adjustmentStatus:', data.adjustmentStatus);
    console.log(
      'Current jobAdjustmentModalVisible state:',
      jobAdjustmentModalVisible
    );

    // 设置本地存储并隐藏下拉菜单
    localStorage.setItem(JOB_ADJUSTMENT_WORKSPACE_FILTER, defaultFilter);
    setVisible(false);
    console.log('job adjustment status ', data.adjustmentStatus);
    // 如果状态是 NONE, DECLINED 或 ACCEPTED，需要先创建调整数据
    if (
      [
        JobAdjustmentStatusEnum.NONE,
        JobAdjustmentStatusEnum.DECLINED,
        JobAdjustmentStatusEnum.ACCEPTED,
      ].includes(data.adjustmentStatus)
    ) {
      console.log('Creating job adjustment for status:', data.adjustmentStatus);
      await workspaceService.jobAdjustments(data.id);
      // 等待一段时间确保数据创建完成
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    // 设置模态框可见
    console.log(
      'Setting jobAdjustmentModalVisible to true for job ID:',
      data.id
    );
    setJobAdjustmentModalVisible(true);

    // 立即检查状态是否更新
    setTimeout(() => {
      console.log(
        'jobAdjustmentModalVisible after setState:',
        jobAdjustmentModalVisible
      );
    }, 100);
  };

  const { run: jumpJobAdjust, loading: jumpJobAdjustLoading } = useRequest(
    jumpJobAdjustFn,
    { manual: true }
  );

  const { cancelJob: jumpJobCancel, loading: jumpJobCancelLoading } =
    useJobCancelJob(
      data,
      () => {
        setJobAdjustmentModalVisible(true);
      },
      // the aggregate effect below owns the page spinner for all four actions
      false
    );

  // the API moves the job onto the RFM board before it returns, so the list can be refreshed
  // straight away
  const sendJobToRFMFn = async () => {
    await workspaceService.sendToRFM(data.id);
    setVisible(false);
    workspace.reloadJobList();
    workspace.getJobCountByStatusFilter(statusFilters);
  };

  const { run: sendJobToRFM, loading: sendJobToRFMLoading } = useRequest(
    sendJobToRFMFn,
    { manual: true }
  );

  const updateJobHandlerFn = async () => {
    const res: any = await jobConfig.jobRevisionAdd(data.id);
    history.push(
      `/job-config/expert/${res.jobConfigId}?isUpdate=${[
        JobUIStatusEnum.PROOFING,
      ].includes(defaultFilter)}`
    );
  };

  const { run: updateJobHandler, loading: updateJobHandlerLoading } =
    useRequest(updateJobHandlerFn, { manual: true });

  useEffect(() => {
    if (
      jumpJobAdjustLoading ||
      jumpJobCancelLoading ||
      updateJobHandlerLoading ||
      sendJobToRFMLoading
    ) {
      app.showLoading();
    } else {
      app.hideLoading();
    }
  }, [
    app,
    jumpJobAdjustLoading,
    jumpJobCancelLoading,
    updateJobHandlerLoading,
    sendJobToRFMLoading,
  ]);
  const actionVisible = useMemo(() => {
    return (
      !(onlyOneParticipant && (hasCustomer || hasProxy)) ||
      workflowStatus !== COMPLETED
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const actionsMap: { [key in JobCardActionEnum]: ActionMap } = {
    [UpdateJob]: {
      text: UpdateJob,
      handler: updateJobHandler,
    },
    [AdjustJob]: {
      text: AdjustJob,
      handler: jumpJobAdjust,
    },
    [SendToRFM]: {
      text: SendToRFM,
      handler: sendJobToRFM,
    },
    [CancelJob]: {
      text: CancelJob,
      handler: () => {
        setVisible(false);
        jumpJobCancel();
      },
    },
  };

  const genActionBtn = (): ActionMap[] =>
    getJobCardActions({
      workflowStatus,
      onlyOneParticipant,
      hasCustomer,
      hasProxy,
      hasOperator,
      hasProducer,
      isInternalJob,
    }).map((key) => actionsMap[key]);

  const hasActions = filterTabs.tabs?.some((tab: FilterTabEnum) => {
    // console.log('actionVisible : ', actionVisible);
    return (
      tab === FilterTabEnum.MORE_ACTIONS &&
      actionVisible &&
      !!genActionBtn().length
    );
  });

  const renderActionBtn = () => {
    let btns = genActionBtn();
    // console.log('dataId : ' + data.id + ' ,Generated buttons:', btns);
    // console.log('directlyAddToCart value:', directlyAddToCart);
    if (directlyAddToCart) {
      btns = btns.filter(({ text }) => {
        return text !== UpdateJob && text !== AdjustJob;
      });
    }
    return (
      <>
        {btns.map(({ text, handler }, index) => {
          // console.log('Rendering button:', text, 'handler:', handler);
          return (
            <div key={index} className="workspace-filter-tabs dropdown">
              <Text
                onClick={() => {
                  console.log('Button clicked:', text);
                  handler();
                }}
              >
                {t(`workspace.tabs.moreActions.${text}` as TFuncKey)}
              </Text>
            </div>
          );
        })}
      </>
    );
  };

  const handleJobAdjustmentModalSuccess = () => {
    console.log('Modal success callback - reloading job list');
    workspace.reloadJobList();
    workspace.getJobCountByStatusFilter(statusFilters);
  };

  return (
    <div
      className={cs('workspace-filter-tabs', {
        'has-actions': hasActions,
      })}
    >
      {filterTabs.tabs?.map((tab: FilterTabEnum) => {
        const isTabDisabled = disabled || disabledTabs.includes(tab);
        return tab === FilterTabEnum.MORE_ACTIONS ? (
          hasActions && (
            <Dropdown
              key={tab}
              trigger={['click']}
              placement="bottomCenter"
              visible={visible}
              onVisibleChange={setVisible}
              align={{ offset: [15, 2] }}
              overlay={
                <RevxOverlay style={{ padding: 12 }}>
                  {renderActionBtn()}
                </RevxOverlay>
              }
            >
              <div
                key={tab}
                className={classNames('workspace-filter-tabs-item', 'actions')}
              >
                {(filterTabs.tabDisplays as { [index: string]: string })[tab]}{' '}
                <ArrowDownIcon />
              </div>
            </Dropdown>
          )
        ) : (
          <div
            key={tab}
            className={classNames('workspace-filter-tabs-item', {
              active: tab === curTab,
              disabled: isTabDisabled,
            })}
            onClick={() => {
              if (isTabDisabled) return;
              let newTab;
              if (curTab === tab) {
                newTab = null;
              } else {
                newTab = tab;
              }

              setCurTab(newTab);
              if (onChange) {
                onChange(newTab);
              }
            }}
          >
            <JobTabDisplay
              tabDisplays={filterTabs.tabDisplays}
              tab={tab}
              job={data}
            />
          </div>
        );
      })}
      <JobAdjustmentModal
        visible={jobAdjustmentModalVisible}
        jobId={data.id}
        onCancel={() => {
          console.log('JobAdjustmentModal onCancel called');
          setJobAdjustmentModalVisible(false);
        }}
        onSuccess={handleJobAdjustmentModalSuccess}
      />
    </div>
  );
};
